import { useState } from "react"
import { XMarkIcon, ChevronUpDownIcon } from "@heroicons/react/16/solid"

export type MultiSelectOption = { value: string; label: string }

type Props = {
  values: string[]
  onChange: (v: string[]) => void
  options: MultiSelectOption[]
  placeholder: string
}

export const MultiSelect = ({ values, onChange, options, placeholder }: Props) => {
  const [open, setOpen] = useState(false)

  const selected = options.filter((o) => values.includes(o.value))

  return (
    <div className="relative">
      {/* trigger */}
      <div
        onClick={() => setOpen((v) => !v)}
        className="min-h-[42px] border border-gray-200 rounded-lg px-3 py-2 cursor-pointer flex items-center justify-between gap-2 hover:border-blue-300 transition-colors"
      >
        <div className="flex flex-wrap gap-1.5 flex-1">
          {selected.length === 0 ? (
            <span className="text-sm text-gray-400">{placeholder}</span>
          ) : (
            selected.map((o) => (
              <span
                key={o.value}
                className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs font-medium px-2 py-0.5 rounded-full"
              >
                {o.label}
                <button
                  onClick={(e) => {
                    e.stopPropagation()
                    onChange(values.filter((v) => v !== o.value))
                  }}
                  className="hover:text-blue-900 transition-colors"
                >
                  <XMarkIcon className="w-3 h-3" />
                </button>
              </span>
            ))
          )}
        </div>
        <ChevronUpDownIcon className="w-4 h-4 text-gray-400 flex-shrink-0" />
      </div>

      {/* dropdown */}
      {open && (
        <div className="absolute z-20 mt-1 w-full bg-white border border-gray-200 rounded-xl shadow-lg py-1 max-h-52 overflow-y-auto">
          {options.length === 0 ? (
            <p className="text-xs text-gray-400 px-3 py-2">Нет вариантов</p>
          ) : (
            options.map((o) => {
              const checked = values.includes(o.value)
              return (
                <label
                  key={o.value}
                  className="flex items-center gap-2.5 px-3 py-2 hover:bg-gray-50 cursor-pointer transition-colors"
                >
                  <div
                    className={`w-4 h-4 rounded border flex items-center justify-center flex-shrink-0 transition-colors ${
                      checked ? "bg-blue-600 border-blue-600" : "border-gray-300"
                    }`}
                  >
                    {checked && (
                      <svg className="w-2.5 h-2.5 text-white" fill="none" viewBox="0 0 10 8">
                        <path d="M1 4l3 3 5-6" stroke="currentColor" strokeWidth={1.5} strokeLinecap="round" strokeLinejoin="round" />
                      </svg>
                    )}
                  </div>
                  <span className="text-sm text-gray-700">{o.label}</span>
                  <input
                    type="checkbox"
                    className="hidden"
                    checked={checked}
                    onChange={() =>
                      onChange(checked ? values.filter((v) => v !== o.value) : [...values, o.value])
                    }
                  />
                </label>
              )
            })
          )}
        </div>
      )}
    </div>
  )
}
